import { useRef, useState } from "react";
import { shelf } from "@pagemates/shared/copy";

const txt = shelf.stars;

function fillFor(i: number, value: number) {
  const f = Math.max(0, Math.min(1, value - i));
  return `${Math.round(f * 100)}%`;
}

export function Stars({ value, size }: { value: number; size?: "sm" }) {
  return (
    <span className={`stars${size ? ` ${size}` : ""}`} role="img" aria-label={txt.outOf5Stars(value)}>
      {[0, 1, 2, 3, 4].map((i) => <span key={i} className="st" style={{ "--fill": fillFor(i, value) } as React.CSSProperties} />)}
    </span>
  );
}

/**
 * Five stars you can tap or drag across. The value is read from where your
 * finger or pointer is along the row, in half stars, so a half rating is a
 * slide, not a precise tap on half a glyph. Shows the value while you move.
 * Keyboard: arrows step by half a star, Enter or Space commits.
 */
export function StarPicker({ value, onChange, disabled }: { value: number | null; onChange: (v: number) => void; disabled?: boolean }) {
  const ref = useRef<HTMLSpanElement>(null); // the five stars only, not the value readout beside them
  const [preview, setPreview] = useState<number | null>(null);
  const dragging = useRef(false);
  const shown = preview ?? value ?? 0;

  const valueAt = (clientX: number) => {
    const r = ref.current!.getBoundingClientRect();
    const f = (clientX - r.left) / r.width;
    return Math.max(0.5, Math.min(5, Math.ceil(f * 10) / 2));
  };
  const onPointerDown = (e: React.PointerEvent<HTMLSpanElement>) => {
    if (disabled) return;
    dragging.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    setPreview(valueAt(e.clientX));
  };
  const onPointerMove = (e: React.PointerEvent<HTMLSpanElement>) => {
    if (disabled) return;
    if (dragging.current || e.pointerType === "mouse") setPreview(valueAt(e.clientX));
  };
  const onPointerUp = (e: React.PointerEvent<HTMLSpanElement>) => {
    if (!dragging.current) return;
    dragging.current = false;
    const v = valueAt(e.clientX);
    setPreview(null);
    if (v !== value) onChange(v);
  };
  const onKey = (e: React.KeyboardEvent<HTMLSpanElement>) => {
    const cur = preview ?? value ?? 0;
    if (e.key === "ArrowRight" || e.key === "ArrowUp") { e.preventDefault(); setPreview(Math.min(5, cur + 0.5)); }
    else if (e.key === "ArrowLeft" || e.key === "ArrowDown") { e.preventDefault(); setPreview(Math.max(0.5, cur - 0.5)); }
    else if ((e.key === "Enter" || e.key === " ") && preview !== null) { e.preventDefault(); onChange(preview); setPreview(null); }
  };

  return (
    <span className={`starpick${disabled ? " disabled" : ""}${preview !== null ? " live" : ""}`} role="slider" tabIndex={disabled ? -1 : 0}
      aria-label={txt.yourRating} aria-valuemin={0.5} aria-valuemax={5} aria-valuenow={shown || undefined} aria-valuetext={shown ? txt.stars(shown) : txt.notRated}
      onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={() => { dragging.current = false; setPreview(null); }}
      onPointerLeave={() => { if (!dragging.current) setPreview(null); }} onKeyDown={onKey} onBlur={() => setPreview(null)}>
      <span className="sts" ref={ref}>{[0, 1, 2, 3, 4].map((i) => <span key={i} className="st" style={{ "--fill": fillFor(i, shown) } as React.CSSProperties} />)}</span>
      <span className="val num" aria-hidden="true">{preview !== null ? preview.toFixed(1) : ""}</span>
    </span>
  );
}
